import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { HeaderActionProvider, useHeaderAction } from "@/contexts/HeaderActionContext";
import { AppShell } from "@/components/layout/AppShell";
import { KPICards } from "@/components/KPICards";
import { DocumentsTable } from "@/components/DocumentsTable";
import { InventoryTable } from "@/components/InventoryTable";
import { ProcessDocumentModal } from "@/components/ProcessDocumentModal";
import { EmbarqueDetailModal } from "@/components/EmbarqueDetailModal";
import { useRegisterHeaderAction } from "@/hooks/useRegisterHeaderAction";
import ExecutiveSummary from "@/pages/ExecutiveSummary";
import Directorio from "@/pages/Directorio";
import Equipo from "@/pages/Equipo";
import { KanbanBoard } from "@/components/KanbanBoard";
import { DashboardKPIs } from "@/components/DashboardKPIs";
import ProformaSimulatorPanel from "@/components/ProformaSimulatorPanel";
import { PlanGate } from "@/components/PlanGate";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useDocuments, useInventory } from "@/hooks/useDocuments";
import { useDocumentTypes, toSlug } from "@/hooks/useDocumentTypes";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { BottomNav } from "@/components/BottomNav";
import { MobileFAB } from "@/components/MobileFAB";



const Dashboard = () => {
  const { section } = useParams<{ section: string }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [filteredDocIds, setFilteredDocIds] = useState<string[] | null>(null);
  const { toast, dismiss } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data: docTabs = [], isLoading: tabsLoading } = useDocumentTypes();

  // Build a map from slug → tipoDocumento
  const slugMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const tab of docTabs) {
      map[tab.slug] = tab.tipoDocumento;
    }
    return map;
  }, [docTabs]);

  // Build title map
  const titleMap = useMemo(() => {
    const map: Record<string, string> = {
      resumen: "Resumen Ejecutivo", directorio: "Datos Maestros", embarques: "Torre de Control", equipo: "Mi Equipo",
      simulador: "Simulador Pro-Forma",
      mensajeria: "Mensajería y Colaboración", tracking: "Tracking de Contenedores", integraciones: "API e Integraciones",
    };
    for (const tab of docTabs) {
      map[tab.slug] = tab.title;
    }
    return map;
  }, [docTabs]);

  const isExecutive = section === "resumen";
  const isDirectorio = section === "directorio";
  const isEmbarques = section === "embarques";
  const isEquipo = section === "equipo";
  const isSimulador = section === "simulador";
  const isPremiumSection = section === "mensajeria" || section === "tracking" || section === "integraciones";
  const docType = section ? slugMap[section] : undefined;
  const sectionTitle = section ? titleMap[section] : undefined;

  const skipDocQuery = isExecutive || isDirectorio || isEmbarques || isEquipo || isSimulador || isPremiumSection;
  const { data: documents = [], isLoading: docsLoading } = useDocuments(skipDocQuery ? undefined : docType);
  const documentIds = documents.map(d => d.id);
  const { data: inventory = [], isLoading: invLoading } = useInventory(skipDocQuery ? undefined : documentIds);

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('doc-inserts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'registro_documentos',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const doc = payload.new as any;
          const tipoDoc = doc.tipo_documento || "Sin tipo";
          const slug = toSlug(tipoDoc);

          toast({
            title: "✅ Documento procesado",
            description: `Se registró "${doc.no_documento}" (${tipoDoc}). Haz clic para ir a la pestaña.`,
            duration: Infinity,
            action: (
              <Button
                variant="outline"
                size="sm"
                className="border-primary text-primary hover:bg-primary/10 text-xs"
                onClick={() => {
                  dismiss();
                  navigate(`/dashboard/${slug}`);
                }}
              >
                Ir a {tipoDoc}
              </Button>
            ),
          });
          queryClient.invalidateQueries({ queryKey: ["documents"] });
          queryClient.invalidateQueries({ queryKey: ["inventory"] });
          queryClient.invalidateQueries({ queryKey: ["monthly-credits"] });
          queryClient.invalidateQueries({ queryKey: ["document-types"] });
          queryClient.invalidateQueries({ queryKey: ["all-documents"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, toast, queryClient, navigate]);

  // Reset selection when switching sections
  useEffect(() => {
    setSelectedDocumentId(null);
    setFilteredDocIds(null);
  }, [section]);

  const handleFilteredIdsChange = useCallback((ids: string[]) => {
    setFilteredDocIds(ids);
  }, []);

  // Cross-filter inventory: first by filtered docs from table, then by selected document
  const filteredInventory = useMemo(() => {
    let result = inventory;
    if (filteredDocIds !== null) {
      const idSet = new Set(filteredDocIds);
      result = result.filter(item => idSet.has(item.documento_id));
    }
    if (selectedDocumentId) {
      result = result.filter(item => item.documento_id === selectedDocumentId);
    }
    return result;
  }, [inventory, filteredDocIds, selectedDocumentId]);

  // KPI data source: selection > filtered > global
  const { kpiDocuments, kpiMode } = useMemo(() => {
    if (selectedDocumentId) {
      const selected = documents.filter(d => d.id === selectedDocumentId);
      return { kpiDocuments: selected, kpiMode: "selection" as const };
    }
    if (filteredDocIds !== null && filteredDocIds.length !== documents.length) {
      const idSet = new Set(filteredDocIds);
      return { kpiDocuments: documents.filter(d => idSet.has(d.id)), kpiMode: "filtered" as const };
    }
    return { kpiDocuments: documents, kpiMode: "global" as const };
  }, [documents, selectedDocumentId, filteredDocIds]);

  // While tabs are loading, show nothing to avoid premature redirect
  if (tabsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  // Validate section
  if (!section || (!isExecutive && !isDirectorio && !isEmbarques && !isEquipo && !isSimulador && !isPremiumSection && !slugMap[section])) {
    return <Navigate to="/dashboard/resumen" replace />;
  }

  return (
    <AppShell>
      <HeaderActionProvider>
        <DashboardInner
          isMobile={isMobile}
          sectionTitle={sectionTitle}
          section={section}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          isExecutive={isExecutive}
          isDirectorio={isDirectorio}
          isEquipo={isEquipo}
          isEmbarques={isEmbarques}
          isSimulador={isSimulador}
          isPremiumSection={isPremiumSection}
          docType={docType}
          kpiDocuments={kpiDocuments}
          kpiMode={kpiMode}
          documents={documents}
          docsLoading={docsLoading}
          selectedDocumentId={selectedDocumentId}
          setSelectedDocumentId={setSelectedDocumentId}
          handleFilteredIdsChange={handleFilteredIdsChange}
          inventory={inventory}
          filteredInventory={filteredInventory}
          invLoading={invLoading}
        />
      </HeaderActionProvider>
    </AppShell>
  );
};

/** Inner component so useHeaderAction reads from provider */
function DashboardInner({
  isMobile, sectionTitle, section, modalOpen, setModalOpen,
  isExecutive, isDirectorio, isEquipo, isEmbarques, isSimulador, isPremiumSection,
  docType, kpiDocuments, kpiMode,
  documents, docsLoading, selectedDocumentId, setSelectedDocumentId,
  handleFilteredIdsChange, inventory, filteredInventory, invLoading,
}: any) {
  const { action } = useHeaderAction();
  const [searchParams, setSearchParams] = useSearchParams();

  const deepShipmentId = searchParams.get("shipmentId");
  const deepShowComments = searchParams.get("showComments") === "true";

  const handleCloseDeepModal = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("shipmentId");
      next.delete("showComments");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <>
        <div className="flex-1 flex flex-col min-w-0">
          <header className="sticky top-0 z-30 h-14 flex items-center justify-between border-b border-border bg-background/80 backdrop-blur-md px-4 shrink-0">
            <div className="flex items-center gap-3">
              {!isMobile && <SidebarTrigger className="text-muted-foreground hover:text-foreground" />}
              <h1 className="text-lg font-semibold text-foreground">{sectionTitle}</h1>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <ThemeToggle />
              {!isMobile && action}
            </div>
          </header>

          {isExecutive ? (
            <ExecutiveSummary />
          ) : isDirectorio ? (
            <Directorio />
          ) : isEquipo ? (
            <Equipo />
          ) : isEmbarques ? (
            <EmbarquesSection isMobile={isMobile} setModalOpen={setModalOpen} />
          ) : isSimulador ? (
            <SimuladorSection setModalOpen={setModalOpen} />
          ) : section === "mensajeria" ? (
            <PlanGate allowedPlans={["PRO", "ENTERPRISE"]} featureName="Mensajería y Colaboración">
              <main className="flex-1 p-6"><p className="text-muted-foreground">Módulo de mensajería — próximamente.</p></main>
            </PlanGate>
          ) : section === "tracking" ? (
            <PlanGate allowedPlans={["ENTERPRISE"]} featureName="Tracking de Contenedores en Tiempo Real">
              <main className="flex-1 p-6"><p className="text-muted-foreground">Módulo de tracking — próximamente.</p></main>
            </PlanGate>
          ) : section === "integraciones" ? (
            <PlanGate allowedPlans={["ENTERPRISE"]} featureName="API y Conexión ERP">
              <main className="flex-1 p-6"><p className="text-muted-foreground">Módulo de integraciones — próximamente.</p></main>
            </PlanGate>
          ) : (
            <DocSection
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              isMobile={isMobile}
              docType={docType}
              kpiDocuments={kpiDocuments}
              kpiMode={kpiMode}
              documents={documents}
              docsLoading={docsLoading}
              selectedDocumentId={selectedDocumentId}
              setSelectedDocumentId={setSelectedDocumentId}
              handleFilteredIdsChange={handleFilteredIdsChange}
              inventory={inventory}
              filteredInventory={filteredInventory}
              invLoading={invLoading}
            />
          )}
        </div>

      {isMobile && <MobileFAB onClick={() => setModalOpen(true)} />}
      {isMobile && <BottomNav />}
      <ProcessDocumentModal open={modalOpen} onOpenChange={setModalOpen} />

      {/* Global deep-linked EmbarqueDetailModal */}
      {deepShipmentId && (
        <EmbarqueDetailModal
          embarqueId={deepShipmentId}
          open
          onOpenChange={(open) => { if (!open) handleCloseDeepModal(); }}
          defaultShowComments={deepShowComments}
        />
      )}
    </>
  );
}

/** Document list view — registers its own header action */
function DocSection({ modalOpen, setModalOpen, isMobile, docType, kpiDocuments, kpiMode, documents, docsLoading, selectedDocumentId, setSelectedDocumentId, handleFilteredIdsChange, inventory, filteredInventory, invLoading }: any) {
  const actionNode = useMemo(
    () => (
      <Button onClick={() => setModalOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2">
        <Upload className="h-4 w-4" />
        Procesar Documento
      </Button>
    ),
    [setModalOpen]
  );
  useRegisterHeaderAction(actionNode);

  return (
    <main className={`flex-1 p-6 space-y-6 overflow-auto ${isMobile ? "pb-36" : ""}`}>
      <div className="animate-fade-in">
        <KPICards documents={kpiDocuments} documentType={docType || ""} mode={kpiMode} />
      </div>
      <div className="space-y-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
        <DocumentsTable
          documents={documents}
          loading={docsLoading}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={setSelectedDocumentId}
          documentType={docType}
          onFilteredIdsChange={handleFilteredIdsChange}
          inventoryItems={inventory}
        />
        <InventoryTable
          items={filteredInventory}
          loading={invLoading}
          linkedDocumentId={selectedDocumentId}
          documentType={docType}
        />
      </div>
    </main>
  );
}

/** Embarques view — registers "Procesar Documento" in header */
function EmbarquesSection({ isMobile, setModalOpen }: { isMobile: boolean; setModalOpen: (v: boolean) => void }) {
  const actionNode = useMemo(
    () => (
      <Button onClick={() => setModalOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2">
        <Upload className="h-4 w-4" />
        Procesar Documento
      </Button>
    ),
    [setModalOpen]
  );
  useRegisterHeaderAction(actionNode);

  return (
    <main className={`flex-1 p-6 space-y-6 overflow-auto ${isMobile ? "pb-36" : ""}`}>
      <DashboardKPIs />
      <KanbanBoard />
    </main>
  );
}

/** Simulador view — registers "Procesar Documento" in header */
function SimuladorSection({ setModalOpen }: { setModalOpen: (v: boolean) => void }) {
  const actionNode = useMemo(
    () => (
      <Button onClick={() => setModalOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2">
        <Upload className="h-4 w-4" />
        Procesar Documento
      </Button>
    ),
    [setModalOpen]
  );
  useRegisterHeaderAction(actionNode);
  return <ProformaSimulatorPanel />;
}

export default Dashboard;
